/**
 * Image compression utilities for profile pictures
 * Reduces file size while maintaining visual quality
 */

export interface CompressionResult {
  compressedDataUrl: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeKB?: number;
  format?: 'jpeg' | 'webp';
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 200,
  maxHeight: 200,
  quality: 0.8,
  maxSizeKB: 50,
  format: 'jpeg'
};

/**
 * Compresses an image file to reduce storage space
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }
    
    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        const { width, height } = calculateDimensions(
          img.width,
          img.height,
          opts.maxWidth,
          opts.maxHeight
        );
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Enable high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw and compress the image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try different quality levels to meet size requirements
        compressToTargetSize(canvas, opts)
          .then(compressedDataUrl => {
            const originalSize = file.size;
            const compressedSize = getDataUrlSize(compressedDataUrl);
            const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;
            
            resolve({
              compressedDataUrl,
              originalSize,
              compressedSize,
              compressionRatio
            });
          })
          .catch(reject);
        
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    // Load the image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Calculate optimal dimensions while maintaining aspect ratio
 */
const calculateDimensions = (
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } => {
  let { width, height } = { width: originalWidth, height: originalHeight };
  
  // Scale down if larger than max dimensions
  if (width > maxWidth || height > maxHeight) {
    const aspectRatio = width / height;
    
    if (width > height) {
      width = maxWidth;
      height = width / aspectRatio;
    } else {
      height = maxHeight;
      width = height * aspectRatio;
    }
  }
  
  return {
    width: Math.round(width),
    height: Math.round(height)
  };
};

/**
 * Compress image to target file size by adjusting quality
 */
const compressToTargetSize = async (
  canvas: HTMLCanvasElement,
  options: Required<CompressionOptions>
): Promise<string> => {
  const targetSizeBytes = options.maxSizeKB * 1024;
  let quality = options.quality;
  let dataUrl = '';
  
  // Try different quality levels
  for (let attempt = 0; attempt < 5; attempt++) {
    dataUrl = canvas.toDataURL(`image/${options.format}`, quality);
    const currentSize = getDataUrlSize(dataUrl);
    
    if (currentSize <= targetSizeBytes || quality <= 0.1) {
      break;
    }
    
    // Reduce quality for next attempt
    quality *= 0.8;
  }
  
  // If still too large, try reducing dimensions
  if (getDataUrlSize(dataUrl) > targetSizeBytes && canvas.width > 100) {
    const scaleFactor = 0.8;
    const newWidth = Math.round(canvas.width * scaleFactor);
    const newHeight = Math.round(canvas.height * scaleFactor);
    
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    
    if (tempCtx) {
      tempCanvas.width = newWidth;
      tempCanvas.height = newHeight;
      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = 'high';
      tempCtx.drawImage(canvas, 0, 0, newWidth, newHeight);
      
      dataUrl = tempCanvas.toDataURL(`image/${options.format}`, options.quality);
    }
  }
  
  return dataUrl;
};

/**
 * Calculate the size of a data URL in bytes
 */
const getDataUrlSize = (dataUrl: string): number => {
  // Remove data URL prefix and calculate base64 size
  const base64 = dataUrl.split(',')[1];
  return Math.round((base64.length * 3) / 4);
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Validate image file before compression
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please select a valid image file' };
  }
  
  // Check file size (max 10MB before compression)
  const maxSizeBytes = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSizeBytes) {
    return { valid: false, error: 'Image must be smaller than 10MB' };
  }
  
  // Check supported formats
  const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  if (!supportedFormats.includes(file.type)) {
    return { valid: false, error: 'Supported formats: JPEG, PNG, WebP, GIF' };
  }
  
  return { valid: true };
};